import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { BrandLogo } from "@/components/BrandLogo";
import { LocaleSwitcher } from "@/components/LocaleSwitcher";
import { ApiPaths, api } from "@/lib/api";

/** Forgot-password — captures an email, fires
 * POST /v1/auth/password-reset/request, then always shows the
 * "check your inbox" confirmation (regardless of whether the email
 * was actually registered, so the page can't be used to enumerate
 * users). */
type FormValues = { email: string };

// Keep in sync with DEFAULT_TTL_MINUTES in
// apps/accounts/services/password_reset.py — used only for the confirmation copy.
const RESET_TTL_MINUTES = 120;

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const { register, handleSubmit, formState } = useForm<FormValues>({
    defaultValues: { email: "" },
  });
  const [submitted, setSubmitted] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const [serverError, setServerError] = useState<string | null>(null);

  const onSubmit = async (values: FormValues) => {
    setServerError(null);
    try {
      await api.post(ApiPaths.authPasswordResetRequest, {
        email: values.email,
      });
      setSentTo(values.email.trim());
      setSubmitted(true);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setServerError(msg || t("auth.resetRequestError"));
    }
  };

  return (
    <main className="min-h-dvh bg-nb-bg py-10">
      <div className="mx-auto max-w-md px-6">
        <div className="mb-6 flex justify-center">
          <BrandLogo />
        </div>
        <div className="rounded-xl border border-nb-border bg-nb-surface/30 p-8">
          <div className="flex items-start justify-between gap-3">
            <h1 className="font-display text-2xl font-semibold text-nb-text">
              {t("auth.forgotPasswordTitle")}
            </h1>
            <LocaleSwitcher />
          </div>
          <p className="mt-2 text-sm text-nb-text-2">
            {t("auth.forgotPasswordSubhead")}
          </p>

          {submitted ? (
            <div className="mt-6 rounded-md border border-nb-mint/40 bg-nb-mint/5 p-4 text-sm text-nb-text">
              {t("auth.resetSentBody", { minutes: RESET_TTL_MINUTES })}
              {sentTo && (
                <div className="mt-2 break-all font-mono text-xs text-nb-text-2">
                  {sentTo}
                </div>
              )}
            </div>
          ) : (
            <form className="mt-6 space-y-5" onSubmit={handleSubmit(onSubmit)}>
              <div>
                <label className="label" htmlFor="email">{t("auth.email")}</label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  className="input"
                  autoFocus
                  {...register("email", { required: true })}
                />
              </div>

              {serverError && (
                <p role="alert" className="text-sm text-nb-danger">
                  {serverError}
                </p>
              )}

              <button
                type="submit"
                className="btn-primary w-full"
                disabled={formState.isSubmitting}
              >
                {formState.isSubmitting
                  ? t("common.loading")
                  : t("auth.resetRequestSubmit")}
              </button>
            </form>
          )}

          <p className="mt-6 text-center text-xs text-nb-text-2">
            <a className="text-nb-mint hover:underline" href="/login">
              {t("auth.backToSignIn")}
            </a>
          </p>
        </div>
      </div>
    </main>
  );
}
